'use client';

import React from 'react';

import Image from 'next/image';
import Link from 'next/link';

import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { type Theme, useTheme } from '@mui/material/styles';

import { MarketplaceIcon } from '@/components/MarketplaceIcon';
import {
  OFFER_CARD_CONTENT_SX,
  TableOfferCardPresentation,
} from '@/components/TableOfferCardPresentation';
import { useUserInfo } from '@/context/UserInfoContext';
import { traceAction } from '@/feedback/actionBreadcrumb';
import { FEEDBACK_ACTION, FEEDBACK_SOURCE, type FeedbackSource } from '@/feedback/ontology/types';
import { updateCollections } from '@/lib/api';
import type { DisplayConceptRef, DisplayImage, DisplayOffer } from '@/lib/api';
import { cdnAwareLoader, DISPLAY_IMAGE_WIDTHS, getSizedImageUrl } from '@/lib/displayImage';
import { sku_url } from '@/lib/utils';

interface OfferMiniCardSharedProps {
  variant: 'floating' | 'table' | 'inline';
  comments?: string;
}

/** Concept-backed offer card contract, including collection behavior and image fallback. */
interface OfferMiniCardProps extends OfferMiniCardSharedProps {
  concept: DisplayConceptRef;
  offer?: DisplayOffer;
  onStarClick?: (cref: string, isInCollection: boolean) => void;
}

type OfferMiniCardVariant = OfferMiniCardProps['variant'];

const PRODUCT_MINI_CARD_IMAGE_WIDTH_BY_VARIANT: Record<OfferMiniCardVariant, number> = {
  floating: DISPLAY_IMAGE_WIDTHS.thumbnail,
  table: DISPLAY_IMAGE_WIDTHS.thumbnail,
  inline: DISPLAY_IMAGE_WIDTHS.compact,
};

function getProductMiniCardImageSizes(variant: OfferMiniCardVariant, theme: Theme): string {
  const smMax = theme.breakpoints.values.sm - 1;
  const mdMax = theme.breakpoints.values.md - 1;

  switch (variant) {
    case 'floating':
    case 'table':
      return `(max-width: ${smMax}px) 84px, 120px`;
    case 'inline':
      return `(max-width: ${mdMax}px) 64px, 72px`;
  }
}

interface OfferImageProps {
  sourceImage?: DisplayImage;
  alt: string;
  requestWidth: number;
  sizes: string;
  objectFit?: 'contain' | 'cover';
}

/** Renders a sized offer image or a deterministic accessible placeholder. */
export function OfferImage({
  sourceImage,
  alt,
  requestWidth,
  sizes,
  objectFit = 'contain',
}: OfferImageProps) {
  return sourceImage ? (
    <Image
      loader={cdnAwareLoader}
      src={getSizedImageUrl(sourceImage, requestWidth)}
      alt={alt}
      fill
      style={{ objectFit }}
      sizes={sizes}
    />
  ) : (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        No Image
      </Typography>
    </Box>
  );
}

// TODO(2): There are lots of places in the code where we have a star icon like
// this that lets the user favorite offers.  We should find all those and
// consolidate them into one component.  There will need to be some variation as
// it appears differently in different contexts, but a lot of the behavior
// should be shareable.
const StyledStarButton: React.FC<{
  isInCollection: boolean;
  onClick: (e: React.MouseEvent) => void;
  size?: 'small' | 'medium';
}> = ({ isInCollection, onClick, size = 'small' }) => {
  const theme = useTheme();

  return (
    <IconButton
      size={size}
      onClick={onClick}
      aria-label={isInCollection ? 'Remove from favorites' : 'Add to favorites'}
      title={isInCollection ? 'Remove from favorites' : 'Add to favorites'}
      sx={{
        position: 'absolute',
        top: 4,
        right: 4,
        padding: size === 'small' ? '4px' : '8px',
        color: 'gold',
        zIndex: 2,
        backgroundColor: 'rgba(128, 128, 128, 0.8)',
        borderRadius: '50%',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        transition: 'background-color 0.2s, transform 0.2s',
        '&:hover': {
          backgroundColor: 'rgba(128, 128, 128, 0.95)',
          transform: 'scale(1.1)',
        },
        [theme.breakpoints.down('sm')]: {
          top: 2,
          right: 2,
          padding: '2px',
          '& svg': {
            fontSize: 16,
          },
        },
      }}
    >
      {isInCollection ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
    </IconButton>
  );
};

export const OfferMiniCard: React.FC<OfferMiniCardProps> = (props) => {
  const { variant, comments, concept, offer, onStarClick } = props;
  const { userInfo, reloadUserInfo } = useUserInfo();
  const theme = useTheme();
  const targetCref = concept.cref;
  const title = concept.display;
  const imageAlt = concept.mention || concept.display;
  const previewImage = offer?.images[0] ?? concept.preview_image ?? undefined;
  const favoriteEligible = concept.is_offer;
  const conceptCref = concept.cref;

  const isInCollection = Boolean(
    userInfo &&
    conceptCref &&
    userInfo.collections.some((collection) => collection.offers.some((p) => p.cref === conceptCref))
  );

  const handleStarClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (onStarClick) {
      onStarClick(concept.cref, isInCollection);
      return;
    }

    // Default star click handler
    if (!userInfo) return;

    try {
      const action = isInCollection ? 'remove' : 'add';
      // Feedback breadcrumb: record the offer-save attempt and its success/failure outcome.
      await traceAction(
        isInCollection ? FEEDBACK_ACTION.UNSAVE_PRODUCT : FEEDBACK_ACTION.SAVE_PRODUCT,
        async () => {
          await updateCollections({
            name: '',
            action,
            docs: [],
            offers: [concept.cref],
          });

          await reloadUserInfo();
        },
        { source: FEEDBACK_SOURCE.PRODUCT_MINI_CARD } satisfies { source: FeedbackSource }
      );
    } catch (error) {
      console.error('Error updating collection:', error);
    }
  };

  const offerUrl = sku_url(targetCref);
  const showPrice = offer?.price != null;
  const showStar = favoriteEligible && showPrice && Boolean(userInfo);
  const imageRequestWidth = PRODUCT_MINI_CARD_IMAGE_WIDTH_BY_VARIANT[variant];
  const imageSizes = getProductMiniCardImageSizes(variant, theme);

  const imageElement = (
    <OfferImage
      sourceImage={previewImage}
      alt={imageAlt}
      requestWidth={imageRequestWidth}
      sizes={imageSizes}
    />
  );

  const priceDisplay = showPrice && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: variant === 'floating' ? 0.5 : variant === 'inline' ? 0 : 'auto',
        ml: 'auto',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: '0.95rem',
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.85rem',
          },
        }}
      >
        {offer?.currency === 'USD' ? '$' : offer?.currency || '$'}
        {offer?.price?.toFixed(2)}
      </Typography>
      {offer?.marketplace && <MarketplaceIcon marketplace={offer.marketplace} size={16} />}
    </Box>
  );

  // Common image box component
  const imageBox = (dimensions: any) => (
    <Box
      sx={{
        position: 'relative',
        flexShrink: 0,
        backgroundColor: 'var(--mui-palette-background-paper)',
        ...dimensions,
      }}
    >
      {imageElement}
      {showStar && <StyledStarButton isInCollection={isInCollection} onClick={handleStarClick} />}
    </Box>
  );

  // Common content box component
  const contentBox = (styles: any, children: React.ReactNode) => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        ...styles,
      }}
    >
      {children}
    </Box>
  );

  // Common title typography
  const titleTypography = (lineClamp: number) => (
    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        fontSize: '0.9rem',
        lineHeight: 1.3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: 'vertical',
        wordBreak: 'break-word', // Ensure long words wrap
        [theme.breakpoints.down('sm')]: {
          fontSize: '0.8rem',
        },
      }}
    >
      {title}
    </Typography>
  );

  if (variant === 'floating') {
    return (
      <Card
        data-testid="ProductMiniCard"
        data-image-request-width={String(imageRequestWidth)}
        data-image-sizes={imageSizes}
        elevation={2}
        sx={{
          float: 'right',
          clear: 'right',
          margin: theme.spacing(1.25, 0, 1.25, 1.25),
          width: 240,
          overflow: 'hidden',
          [theme.breakpoints.down('sm')]: {
            width: 168,
            margin: theme.spacing(0.75, 0, 0.75, 0.75),
          },
        }}
      >
        <CardActionArea component={Link} href={offerUrl}>
          <CardContent sx={OFFER_CARD_CONTENT_SX}>
            {imageBox({
              width: 120,
              height: 120,
              [theme.breakpoints.down('sm')]: {
                width: 84,
                height: 84,
              },
            })}
            {contentBox(
              {
                padding: theme.spacing(1),
                width: 120,
                [theme.breakpoints.down('sm')]: {
                  width: 84,
                  padding: theme.spacing(0.75),
                },
              },
              <>
                {titleTypography(4)}
                {priceDisplay}
              </>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  // Inline variant (for context panel)
  if (variant === 'inline') {
    return (
      <Card
        data-testid="ProductMiniCard"
        data-image-request-width={String(imageRequestWidth)}
        data-image-sizes={imageSizes}
        elevation={2}
        sx={{
          overflow: 'hidden',
        }}
      >
        <CardActionArea component={Link} href={offerUrl}>
          <CardContent sx={OFFER_CARD_CONTENT_SX}>
            {imageBox({
              width: 64,
              height: 64,
              [theme.breakpoints.up('md')]: {
                width: 72,
                height: 72,
              },
            })}
            {contentBox(
              {
                padding: theme.spacing(1),
                justifyContent: 'space-between',
              },
              <>
                {titleTypography(3)}
                {priceDisplay}
              </>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  // Table variant
  return (
    <Card
      data-testid="ProductMiniCard"
      data-image-request-width={String(imageRequestWidth)}
      data-image-sizes={imageSizes}
      elevation={2}
      sx={{
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          elevation: 3,
        },
      }}
    >
      <CardActionArea component={Link} href={offerUrl}>
        <TableOfferCardPresentation
          image={imageElement}
          overlay={
            showStar ? (
              <StyledStarButton isInCollection={isInCollection} onClick={handleStarClick} />
            ) : undefined
          }
          title={title}
          comments={comments}
          footer={priceDisplay}
        />
      </CardActionArea>
    </Card>
  );
};
